import React, { useEffect, useState } from 'react';
import { FaAddressCard, FaCalendar, FaCheck, FaEnvelope, FaFemale, FaIdCard, FaLock, FaMale, FaPhone, FaTimes, FaUser, FaUserTie, FaPalette } from 'react-icons/fa';
import { FaCircleInfo } from 'react-icons/fa6';
import { getPhotoUrl } from '../services/utils/supabaseUtils';
import { getRoles } from '../services/api/memberService';
import CommonButton from './CommonButton';
import CustomFormGroup from './CustomFormGroup';
import LanguageToggle from './LanguageToggle';
import SkillModal from "./SkillModal";
import { useLoading } from "./LoadingContext";

const MemberForm = ({ initialData = {}, onSubmit, onCancel, isEdit = false, member_type }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    nick_name: '',
    email: '',
    phone: '',
    gender: '',
    dob: '',
    address: '',
    password: '',
    id_card_number: '',
    id_card: null,
    photo: null,
    status:'1',
    inactive_reason: '',
    role_id: 3,
    member_type: member_type,
    skills: [],
    ...initialData,
  });

  const user = JSON.parse(localStorage.getItem('user'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [language, setLanguage] = useState('ID');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [idCardPreview, setIdCardPreview] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const { showLoading, hideLoading } = useLoading();

  const handleOpenSkillModal = () => setShowSkillModal(true);
  const handleCloseSkillModal = () => setShowSkillModal(false);

  const handleSaveSkills = (skills) => {
    setSelectedSkills(skills);
    setFormData((prev) => ({
      ...prev,
      skills: skills.map(s => s.id), // kirim ID saja ke backend
    }));
    setShowSkillModal(false);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await getRoles();
        setRoleOptions(roles.map(role => ({
          label: role.name,
          value: role.id,
        })));
      } catch (error) {
        console.error('Failed to load roles:', error.message);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    if (isEdit && initialData) {
      let formattedDOB = '';

      if (initialData.dob) {
        // support yyyy-mm-dd atau ISO
        const iso = initialData.dob.split('T')[0];
        const [y, m, d] = iso.split('-');

        if (y && m && d) {
          formattedDOB = `${d}/${m}/${y}`; // ✅ UI format
        }
      }

      setFormData(prev => ({
        ...prev,
        ...initialData,
        dob: formattedDOB,
        status: initialData.is_active ? '1' : '0',
        skills: initialData.skills || [],
      }));

      // skill mapping (biarin seperti sekarang)
      if (Array.isArray(initialData.skills)) {
        setSelectedSkills(
          initialData.skills.map(skill =>
            typeof skill === "number"
              ? { id: skill, name: "" }
              : skill
          )
        );
      }

      // photo preview
      if (typeof initialData.photo === 'string') {
        (async () => setPhotoPreview(await getPhotoUrl(initialData.photo)))();
      }

      if (typeof initialData.id_card === 'string') {
        (async () => setIdCardPreview(await getPhotoUrl(initialData.id_card)))();
      }
    }
  }, [initialData, isEdit]);


  const toggleLanguage = () => {
    setLanguage(prev => (prev === 'ID' ? 'EN' : 'ID'));
  };

  const t = (en, id) => (language === 'EN' ? en : id);
  const tfull_name = t('Full Name', 'Nama Lengkap')
  const tnick_name = t('Nick Name', 'Nama Panggilan');
  const tPhone = t('Phone Number', 'No. Tlp');
  const tPhoneValidation = t('Phone Number can only contain number, +, or -', 'Nomor hanya boleh berisi angka, +, atau -');
  const tDOB = t('Date of Birth', 'Tanggal Lahir');
  const tGender = t('Gender', 'Jenis Kelamin');
  const tMale = t('Male', 'Laki-laki');
  const tFemale = t('Female', 'Perempuan');
  const tAddress = t('Address', 'Alamat');
  const tPhoto = t('Upload Photo', 'Unggah Foto');
  const tid_card_number = t('ID Card / KTP / Passport', 'KTP / Passport');
  const tid_card = t('Upload ID Card / KTP / Passport', 'Unggah KTP / Passport');
  const tIsRequired = t('is required', 'wajib diisi');
  const tRegist = t('Register Member', 'Daftar Anggota');
  const tPassword = t('Password', 'Kata Sandi');
  const tStatusActive = t('Active', 'Aktif');
  const tStatusInactive = t('Inactive', 'Tidak Aktif');
  const tReason = t('Reason For Inactive', 'Alasan tidak aktif');
  const tFileSize = t('Maximum file size is 5MB', 'Ukuran file maksimal 5MB');
  const tFile = t('Failed to read file', 'Gagal membaca file');
  const tskills = t('Skills', 'Keahlian');

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setErrors(prevErrors => {
        const updated = {};
        for (const key in prevErrors) {
          if (prevErrors[key]) {
            if (prevErrors[key].includes('required') || prevErrors[key].includes('wajib')) {
              const base =
                key === 'full_name' ? tfull_name :
                key === 'nick_name' ? tnick_name :
                key === 'phone' ? tPhone :
                key === 'dob' ? tDOB :
                key === 'gender' ? tGender :
                key === 'address' ? tAddress :
                key === 'photo' ? tPhoto :
                key === 'id_card_number' ? tid_card_number :
                key === 'id_card' ? tid_card :
                key === 'password' ? tPassword :
                key === 'inactive_reason' ? tReason :
                key === 'skills' ? tskills : key;
              updated[key] = `${base} ${tIsRequired}`;
            } else if (prevErrors[key].includes('file size') || prevErrors[key].includes('Ukuran file')) {
              updated[key] = tFileSize;
            } else if (prevErrors[key].includes('Failed to read') || prevErrors[key].includes('Gagal membaca')) {
              updated[key] = tFile;
            } else if (prevErrors[key].includes('Phone Number') || prevErrors[key].includes('Nomor hanya')) {
              updated[key] = tPhoneValidation;
            } else {
              updated[key] = prevErrors[key];
            }
          }
        }
        return updated;
      });
    }
  }, [language]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone' && !/^[+\-0-9]*$/.test(value)) return; // Batasi input phone
    setFormData((prev) => ({ ...prev, [name]: name === 'role_id' ? Number(value) : value }));
  };

  const handleIdCardChange = async (e) => {
    const file = e.target.files[0];
    const { name } = e.target;
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!file) {
      e.target.value = '';
      setIdCardPreview(null);
      setFormData((prev) => ({ ...prev, [name]: null }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    if (file) {
      if (file.size > maxSize) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: tFileSize,
        }));
        return;
      }
  
      try {
        setFormData((prevData) => ({
          ...prevData,
          [name]: file,
        }));
        setIdCardPreview(URL.createObjectURL(file));
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: '', // Bersihkan error jika valid
        }));
      } catch (err) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: tFile,
        }));
      }
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    const { name } = e.target;
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!file) {
      e.target.value = '';
      setPhotoPreview(null);
      setFormData((prev) => ({ ...prev, [name]: null }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }
  
    if (file) {
      if (file.size > maxSize) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: tFileSize,
        }));
        return;
      }
  
      try {
        setFormData((prevData) => ({
          ...prevData,
          [name]: file,
        }));
        setPhotoPreview(URL.createObjectURL(file));
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: '', // Bersihkan error jika valid
        }));
      } catch (err) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: tFile,
        }));
      }
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(prev => !prev);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name) newErrors.full_name = tfull_name + ' ' + tIsRequired;
    if (!formData.nick_name) newErrors.nick_name = tnick_name + ' ' + tIsRequired;
    if (!formData.email) newErrors.email = 'Email ' + tIsRequired;
    if (!formData.phone) newErrors.phone = tPhone + ' ' + tIsRequired;
    if (!/^[+\-0-9]*$/.test(formData.phone)) newErrors.phone = tPhoneValidation;
    if (!formData.dob) newErrors.dob = tDOB + ' ' + tIsRequired;
    if (!formData.gender) newErrors.gender = tGender + ' ' + tIsRequired;
    if (!formData.address) newErrors.address = tAddress + ' ' + tIsRequired;
    if (!isEdit && !formData.photo && !photoPreview) newErrors.photo = tPhoto + ' ' + tIsRequired;
    if (!formData.id_card_number) newErrors.id_card_number = tid_card_number + ' ' + tIsRequired;
    if (!isEdit && !formData.id_card && !idCardPreview) newErrors.id_card = tid_card + ' ' + tIsRequired;
    if (!formData.password && !isEdit) newErrors.password = tPassword + ' ' + tIsRequired;
    if (isEdit && formData.status === "0" && !formData.inactive_reason) newErrors.inactive_reason = tReason + ' ' + tIsRequired;
    if (formData.skills.length < 1) newErrors.skills = tskills + ' ' + tIsRequired;

    console.log(newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    showLoading();
    try {
      let dobISO = formData.dob;

      // convert dd/mm/yyyy → yyyy-mm-dd
      if (dobISO && dobISO.includes('/')) {
        const [d, m, y] = dobISO.split('/');
        dobISO = `${y}-${m}-${d}`;
      }

      const updatedData = {
        ...formData,
        dob: dobISO,
        skills: selectedSkills.map(s => s.id),
      };

      await onSubmit(updatedData, language);
    } finally {
      hideLoading();
    }
  };


  return (
    <div className='register-page'>
      <div className="register-member-container">
        <div className="register-header">
        <h2>{isEdit ? "Edit Profile" : tRegist}</h2>
          <LanguageToggle lang={language} onToggle={toggleLanguage}/>
        </div>
        <form className="register-form" onSubmit={handleSubmit} autoComplete="off">
          <CustomFormGroup
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder={tfull_name}
            icon={<FaUser />}
            error={errors.full_name}
          />
          <CustomFormGroup
            type="text"
            name="nick_name"
            value={formData.nick_name}
            onChange={handleChange}
            placeholder={tnick_name}
            icon={<FaUser />}
            error={errors.nick_name}
          />
          <CustomFormGroup
            type="date"
            name="dob"
            label={tDOB}
            value={formData.dob || ''}
            onChange={handleChange}
            placeholder={"dd/mm/yyyy"}
            icon={<FaCalendar />}
            error={errors.dob}
          />
          <CustomFormGroup
            label={tGender}
            type="radio"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            required
            options={[
              { label: tMale, value: "M", icon: <FaMale /> },
              { label: tFemale, value: "F", icon: <FaFemale /> },
            ]}
            error={errors.gender}
          />
          <CustomFormGroup
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={tPhone}
            icon={<FaPhone />}
            error={errors.phone}
          />
          <CustomFormGroup
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            icon={<FaEnvelope />}
            error={errors.email}
          />
          {!isEdit && 
            <CustomFormGroup
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              icon={<FaLock />}
              togglePassword
              showPassword={showPassword}
              onTogglePassword={handleTogglePassword}
              error={errors.password}
            />
          } 
          <CustomFormGroup
            type="textarea"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder={tAddress}
            icon={<FaAddressCard />}
            error={errors.address}
            
          />
          <CustomFormGroup
            type="text"
            name="id_card_number"
            value={formData.id_card_number}
            onChange={handleChange}
            placeholder={tid_card_number}
            icon={<FaIdCard />}
            error={errors.id_card_number}
          />
          <CustomFormGroup
            type="file"
            name="id_card"
            onChange={handleIdCardChange}
            label={tid_card}
            icon={<FaIdCard />}
            accept="image/*"
            error={errors.id_card}
            filePreview={idCardPreview}
          />
          <CustomFormGroup
            type="file"
            name="photo"
            onChange={handlePhotoChange}
            label={tPhoto}
            icon={<FaUser />}
            accept="image/*"
            error={errors.photo}
            filePreview={photoPreview}
          />
          {isEdit && (
            <>
            {(user?.role_id === 1 || user?.role_id === 2) && (
              <CustomFormGroup
                label="Role"
                name="role_id"
                type="select"
                options={roleOptions}
                value={formData.role_id}
                onChange={handleChange}
                icon={<FaUserTie/>}
              />
            )}
              <CustomFormGroup
                label="Status"
                type="radio"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                options={[
                  { label: tStatusActive, value: "1", icon: <FaCheck/> },
                  { label: tStatusInactive, value: "0", icon: <FaTimes/> },
                ]}
                error={errors.status}
              />
              {formData.status === "0" && (
                <CustomFormGroup
                  type='textarea'
                  placeholder={tReason}
                  name="inactive_reason"
                  value={formData.inactive_reason}
                  onChange={handleChange}
                  error={errors.inactive_reason}
                  icon={<FaCircleInfo/>}
                />
              )}
            </>
          )}
          <div>
            <div 
              onClick={handleOpenSkillModal} 
              style={{ 
                cursor: "pointer",
                transition: "0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.85)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}
            >
            <CustomFormGroup
              type="chipSelect"
              name="skills"
              value={selectedSkills}
              placeholder={tskills}
              onChange={(e) => {
                const newSkills = e.target.value;
                setSelectedSkills(newSkills);
                setFormData((prev) => ({ ...prev, skills: newSkills.map(s => s.id) }));
              }}
              onOpenPopup={() => setShowSkillModal(true)}
              readOnly
              icon={<FaPalette />}
              error={errors.skills}
            />
          </div>
        </div>

          {showSkillModal && (
            <SkillModal
              selectedSkills={selectedSkills}
              onSave={handleSaveSkills}
              onClose={handleCloseSkillModal}
            />
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <CommonButton
              type="submit"
            >
              Submit
            </CommonButton>
            <CommonButton
              isCancel={true}
              onClick={onCancel}
            >
              Cancel
            </CommonButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberForm;
